/**
 * 全局轻量弹窗服务（纯 JS 动态注入，不依赖 Vue 组件树）
 *
 * - showAlert(message | opts)   → Promise<void>     单按钮提示
 * - showConfirm(message | opts) → Promise<boolean>  确认 / 取消，true=确认
 *
 * 调用时会向 document.body 注入遮罩 + 卡片，关闭后自动移除 DOM。
 * 颜色全部使用 styles/tokens.css 的 CSS 变量，自动跟随 light/dark 主题。
 */

let styleInjected = false
const STACK = []          // 当前打开的实例栈（后入在上层）
const BASE_Z = 200        // 遮罩基础 z-index，逐层递增
const ANIM_MS = 220

/* ---------- 图标（Feather 风格，stroke 使用 currentColor） ---------- */
const ICONS = {
  info: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>',
  success: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>',
  warning: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
  error: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>',
}

function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;')
}

function injectStyle() {
  if (styleInjected || typeof document === 'undefined') return
  styleInjected = true
  const css = `
.crirp-dlg-mask {
  position: fixed; inset: 0; z-index: 200;
  background: rgba(16,24,40,.5);
  -webkit-backdrop-filter: blur(3px); backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
  opacity: 0; transition: opacity .18s ease;
}
.crirp-dlg-mask.show { opacity: 1; }
.crirp-dlg-mask.leaving { opacity: 0; }

.crirp-dlg {
  width: 100%; max-width: 400px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  padding: 28px 26px 22px;
  text-align: center;
  transform: translateY(10px) scale(.96);
  opacity: 0;
  transition: transform ${ANIM_MS}ms cubic-bezier(.2,.9,.3,1), opacity .18s ease;
}
.crirp-dlg-mask.show .crirp-dlg { transform: none; opacity: 1; }

.crirp-dlg-icon {
  width: 54px; height: 54px; margin: 0 auto 14px;
  border-radius: 50%;
  display: grid; place-items: center;
}
.crirp-dlg-icon svg { width: 27px; height: 27px; }
.crirp-dlg-icon.info    { background: var(--primary-soft); color: var(--primary); }
.crirp-dlg-icon.success { background: var(--ok-bg); color: var(--ok-fg); }
.crirp-dlg-icon.warning { background: var(--warn-bg); color: var(--warn-fg); }
.crirp-dlg-icon.error   { background: var(--danger-soft); color: var(--danger); }

.crirp-dlg-title { font-size: 17px; font-weight: 800; color: var(--text); margin-bottom: 8px; }
.crirp-dlg-msg {
  font-size: 14.5px; color: var(--text-2); line-height: 1.65;
  white-space: pre-wrap; word-break: break-word;
}

.crirp-dlg-actions { display: flex; gap: 10px; justify-content: center; margin-top: 22px; }
.crirp-dlg-btn {
  min-width: 96px; height: 40px; padding: 0 18px;
  border-radius: 999px; font-weight: 600; font-size: 14px;
  transition: background .15s, color .15s, border-color .15s, transform .05s, box-shadow .15s, filter .15s;
}
.crirp-dlg-btn.primary { background: var(--primary); color: #fff; }
.crirp-dlg-btn.primary:hover { background: var(--primary-hover); box-shadow: 0 4px 14px rgba(79,70,229,.32); }
.crirp-dlg-btn.danger { background: var(--danger); color: #fff; }
.crirp-dlg-btn.danger:hover { filter: brightness(.92); box-shadow: 0 4px 14px rgba(239,68,68,.32); }
.crirp-dlg-btn.ghost { background: transparent; color: var(--text-2); border: 1px solid var(--border-strong); }
.crirp-dlg-btn.ghost:hover { background: var(--surface-2); color: var(--text); }
.crirp-dlg-btn:active { transform: translateY(1px); }
.crirp-dlg-btn:focus-visible { outline: none; box-shadow: 0 0 0 3px var(--primary-soft); }

@media (max-width: 420px) {
  .crirp-dlg { padding: 24px 20px 20px; }
  .crirp-dlg-btn { flex: 1; min-width: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .crirp-dlg-mask, .crirp-dlg { transition: opacity .12s ease; }
  .crirp-dlg { transform: none !important; }
}`
  const el = document.createElement('style')
  el.id = 'crirp-dialog-style'
  el.textContent = css
  document.head.appendChild(el)
}

/* ---------- 键盘：只作用于栈顶实例 ---------- */
function onKey(e) {
  const top = STACK[STACK.length - 1]
  if (!top) return
  if (e.key === 'Escape') { e.preventDefault(); top.resolve(false) }
  else if (e.key === 'Enter') { e.preventDefault(); top.resolve(true) }
}

function createDialog(opts) {
  injectStyle()

  const type = ICONS[opts.type] ? opts.type : 'info'
  const showCancel = !!opts.showCancel
  const danger = !!opts.danger
  const title = opts.title || ''
  const message = opts.message || ''
  const confirmText = opts.confirmText || (showCancel ? '确定' : '知道了')
  const cancelText = opts.cancelText || '取消'

  let resolveFn
  const promise = new Promise((res) => { resolveFn = res })

  const mask = document.createElement('div')
  mask.className = 'crirp-dlg-mask'
  mask.innerHTML = `
    <div class="crirp-dlg" role="alertdialog" aria-modal="true">
      <div class="crirp-dlg-icon ${type}">${ICONS[type]}</div>
      ${title ? `<div class="crirp-dlg-title">${escapeHtml(title)}</div>` : ''}
      <div class="crirp-dlg-msg">${escapeHtml(message)}</div>
      <div class="crirp-dlg-actions">
        ${showCancel ? `<button type="button" class="crirp-dlg-btn ghost" data-act="cancel">${escapeHtml(cancelText)}</button>` : ''}
        <button type="button" class="crirp-dlg-btn ${danger ? 'danger' : 'primary'}" data-act="ok">${escapeHtml(confirmText)}</button>
      </div>
    </div>`

  const inst = { mask, resolve }
  function close() {
    if (mask.dataset.closed) return
    mask.dataset.closed = '1'
    const i = STACK.indexOf(inst)
    if (i >= 0) STACK.splice(i, 1)
    mask.classList.remove('show')
    mask.classList.add('leaving')
    setTimeout(() => { if (mask.parentNode) mask.parentNode.removeChild(mask) }, ANIM_MS)
  }
  function resolve(value) {
    if (mask.dataset.closed) return
    close()
    resolveFn(value)
  }
  inst.resolve = resolve

  mask.addEventListener('click', (e) => {
    const actEl = e.target.closest('[data-act]')
    if (actEl) {
      resolve(actEl.dataset.act === 'ok')
      return
    }
    // 点击遮罩空白：confirm 视为取消，alert 直接关闭
    if (e.target === mask) resolve(showCancel ? false : true)
  })

  mask.style.zIndex = String(BASE_Z + STACK.length)   // 逐层递增，保证栈顶实例在最上层
  document.body.appendChild(mask)
  STACK.push(inst)
  // 双 rAF 保证初始（隐藏）态先渲染，再切换到 show 触发过渡
  requestAnimationFrame(() => requestAnimationFrame(() => mask.classList.add('show')))

  // 焦点：confirm 默认聚焦「取消」防误删；alert 聚焦「确定」
  const focusSel = showCancel ? '[data-act="cancel"]' : '[data-act="ok"]'
  const btn = mask.querySelector(focusSel)
  if (btn) setTimeout(() => { try { btn.focus({ preventScroll: true }) } catch (_) { /* 元素已移除等，忽略 */ } }, 0)

  return promise
}

/**
 * 提示弹窗（单按钮）
 * @param {string|object} message 或 { type, title, message, confirmText }
 */
export function showAlert(message, opts = {}) {
  const o = typeof message === 'string' ? { message, ...opts } : (message || {})
  return createDialog({ showCancel: false, type: 'info', ...o })
}

/**
 * 确认弹窗（确认 / 取消）
 * @param {string|object} message 或 { type, title, message, confirmText, cancelText, danger }
 * @returns Promise<boolean>  true=确认
 */
export function showConfirm(message, opts = {}) {
  const o = typeof message === 'string' ? { message, ...opts } : (message || {})
  return createDialog({ showCancel: true, type: 'warning', ...o })
}

// 只挂一次全局键盘监听
if (typeof document !== 'undefined') {
  document.addEventListener('keydown', onKey)
}

export default { alert: showAlert, confirm: showConfirm }
